import { Link } from "@tanstack/solid-router";
import { For, Show, createMemo, createSignal } from "solid-js";

import {
  useAgentActions,
  useApproveAgentAction,
  useBulkApproveAgentActions,
  useInvokeBuiltinAgent,
  useRejectAgentAction,
} from "~/lib/hooks";
import type { AgentActionRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

const ACCOUNT_DEDUP_DETECTOR_BUILTIN_KIND = "dedup.account";
const CONTACT_DEDUP_DETECTOR_BUILTIN_KIND = "dedup.contact";
const LEAD_SCORER_BUILTIN_KIND = "scorer.lead";

/**
 * Review Queue — pending agent proposals awaiting human approval.
 *
 * Phase B-1 covers the account-merge case (kind='merge_accounts'). The
 * row renders a side-by-side preview (from → into) with Approve / Reject
 * inline. New kinds plug in by adding a render branch in `<CandidatePreview>`.
 *
 * The widget also exposes a "Scan for duplicates" trigger button so
 * admins can populate the queue without leaving the dashboard.
 */
export function ReviewQueueWidget(props: Props) {
  const pending = useAgentActions(props.wsKey, () => ({
    state: "pending_approval",
    limit: 20,
  }));
  const approveMut = useApproveAgentAction(props.wsKey);
  const rejectMut = useRejectAgentAction(props.wsKey);
  const bulkApproveMut = useBulkApproveAgentActions(props.wsKey);
  const invokeMut = useInvokeBuiltinAgent(props.wsKey);
  const [scanError, setScanError] = createSignal<string | null>(null);
  const [scanSummary, setScanSummary] = createSignal<string | null>(null);
  const [opError, setOpError] = createSignal<string | null>(null);
  const [selected, setSelected] = createSignal<Set<string>>(new Set<string>());

  const runScan = async (builtinKind: string, label: string): Promise<void> => {
    setScanError(null);
    setScanSummary(null);
    try {
      const r = await invokeMut.mutateAsync({ builtinKind });
      // The summary shape varies per agent — render the most relevant
      // counts each one provides.
      if ("scored" in r.summary || "candidates_examined" in r.summary) {
        const scored = r.summary.scored ?? 0;
        const examined = r.summary.candidates_examined ?? 0;
        const failed = r.summary.failed ?? 0;
        setScanSummary(
          `${label}: scored ${scored} of ${examined} contact${examined === 1 ? "" : "s"}${
            failed > 0 ? ` (${failed} failed)` : ""
          }.`,
        );
      } else {
        const proposed = r.summary.candidates_proposed ?? 0;
        const clusters = r.summary.clusters_found ?? 0;
        setScanSummary(
          proposed === 0
            ? `${label}: no new duplicates (${clusters} clusters examined).`
            : `${label}: ${proposed} candidate${proposed === 1 ? "" : "s"} added.`,
        );
      }
    } catch (err) {
      setScanError((err as Error).message);
    }
  };

  const approve = async (row: AgentActionRead) => {
    setOpError(null);
    try {
      await approveMut.mutateAsync({ actionId: row.id });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (err) {
      setOpError((err as Error).message);
    }
  };

  const reject = async (row: AgentActionRead) => {
    const reason = window.prompt("Reason for rejecting? (optional)") ?? null;
    setOpError(null);
    try {
      await rejectMut.mutateAsync({ actionId: row.id, reason });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (err) {
      setOpError((err as Error).message);
    }
  };

  const toggleSelect = (actionId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const visibleIds = createMemo(() => new Set((pending.data?.items ?? []).map((r) => r.id)));
  const visibleSelectedIds = createMemo(() =>
    Array.from(selected()).filter((id) => visibleIds().has(id)),
  );
  const allVisibleSelected = () => {
    const v = visibleIds();
    if (v.size === 0) return false;
    for (const id of v) if (!selected().has(id)) return false;
    return true;
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected()) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds()) next.delete(id);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds()) next.add(id);
        return next;
      });
    }
  };

  const runBulkApprove = async () => {
    const ids = visibleSelectedIds();
    if (ids.length === 0) return;
    setOpError(null);
    try {
      const r = await bulkApproveMut.mutateAsync({ actionIds: ids });
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      if (r.failed > 0) {
        setOpError(
          `Bulk approve: ${r.succeeded} succeeded, ${r.failed} failed. Check the queue for details.`,
        );
      }
    } catch (err) {
      setOpError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 class="card-title text-base">🔍 Review queue</h3>
            <p class="text-xs opacity-60 mt-0.5">AI proposals awaiting your approval.</p>
          </div>
          <div class="flex gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-sm"
              onClick={() => runScan(ACCOUNT_DEDUP_DETECTOR_BUILTIN_KIND, "Accounts")}
              disabled={invokeMut.isPending}
            >
              {invokeMut.isPending ? "Scanning…" : "Scan accounts"}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              onClick={() => runScan(CONTACT_DEDUP_DETECTOR_BUILTIN_KIND, "Contacts")}
              disabled={invokeMut.isPending}
            >
              {invokeMut.isPending ? "Scanning…" : "Scan contacts"}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              onClick={() => runScan(LEAD_SCORER_BUILTIN_KIND, "Lead scoring")}
              disabled={invokeMut.isPending}
              title="Score contacts that haven't been scored in the last 7 days"
            >
              {invokeMut.isPending ? "Working…" : "🎯 Score leads"}
            </button>
          </div>
        </div>

        <Show when={(pending.data?.items.length ?? 0) > 0}>
          <div class="flex items-center gap-2 mt-3 border-t border-base-200 pt-3">
            <label class="label cursor-pointer gap-2 p-0">
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                checked={allVisibleSelected()}
                onChange={toggleSelectAll}
                aria-label="Select all visible candidates"
              />
              <span class="label-text text-xs">
                {visibleSelectedIds().length} of {pending.data?.items.length ?? 0} selected
              </span>
            </label>
            <button
              type="button"
              class="btn btn-success btn-sm ml-auto"
              disabled={visibleSelectedIds().length === 0 || bulkApproveMut.isPending}
              onClick={runBulkApprove}
            >
              {bulkApproveMut.isPending ? "Approving…" : `Approve ${visibleSelectedIds().length}`}
            </button>
          </div>
        </Show>

        <Show when={scanError()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {scanError()}
          </div>
        </Show>

        <Show when={scanSummary()}>
          <div role="status" class="alert alert-info text-sm mt-2">
            {scanSummary()}
          </div>
        </Show>

        <Show when={opError()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {opError()}
          </div>
        </Show>

        <Show when={pending.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={!pending.isLoading && (pending.data?.items.length ?? 0) === 0}>
          <p class="text-sm opacity-60 mt-2">
            Nothing pending — try "Scan for duplicates" to look for merge candidates.
          </p>
        </Show>

        <ul class="mt-2 divide-y divide-base-200">
          <For each={pending.data?.items ?? []}>
            {(row) => (
              <li class="py-3 flex items-start gap-2">
                <input
                  type="checkbox"
                  class="checkbox checkbox-sm mt-1"
                  checked={selected().has(row.id)}
                  onChange={() => toggleSelect(row.id)}
                  aria-label="Select for bulk approve"
                />
                <div class="flex-1 min-w-0">
                  <CandidatePreview
                    row={row}
                    wsKey={props.wsKey}
                    onApprove={() => approve(row)}
                    onReject={() => reject(row)}
                    busy={approveMut.isPending || rejectMut.isPending || bulkApproveMut.isPending}
                  />
                </div>
              </li>
            )}
          </For>
        </ul>
      </div>
    </div>
  );
}

function CandidatePreview(props: {
  row: AgentActionRead;
  wsKey: () => string;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  if (props.row.kind === "merge_accounts" || props.row.kind === "merge_contacts") {
    return <MergePreview {...props} />;
  }
  // Unknown kind — fall back to a generic JSON view so admins still see
  // *something* and can dismiss it.
  return (
    <div>
      <div class="flex items-start justify-between gap-3">
        <div class="flex-1 min-w-0">
          <div class="font-medium text-sm">{props.row.kind}</div>
          <pre class="text-xs opacity-60 bg-base-200 rounded p-2 mt-1 overflow-x-auto max-h-32">
            {JSON.stringify(props.row.output ?? props.row.input, null, 2)}
          </pre>
        </div>
        <ApproveRejectButtons
          onApprove={props.onApprove}
          onReject={props.onReject}
          busy={props.busy}
        />
      </div>
    </div>
  );
}

function MergePreview(props: {
  row: AgentActionRead;
  wsKey: () => string;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  const cand = () => {
    const out = props.row.output;
    if (!out || typeof out !== "object") return null;
    const c = (out as Record<string, unknown>).candidate;
    if (!c || typeof c !== "object") return null;
    return c as {
      from_id?: string;
      from_name?: string;
      into_id?: string;
      into_name?: string;
      reason?: string;
      confidence?: string;
    };
  };

  const isContact = () => props.row.kind === "merge_contacts";
  const subjectLabel = () => (isContact() ? "Merge contacts" : "Merge accounts");

  const linkFor = (id: string | undefined, label: string | undefined) => {
    if (!id) return null;
    if (isContact()) {
      return (
        <Link
          to="/workspaces/$wsKey/contacts/$conId"
          params={{ wsKey: props.wsKey(), conId: id }}
          class="link link-primary truncate"
        >
          {label ?? id}
        </Link>
      );
    }
    return (
      <Link
        to="/workspaces/$wsKey/accounts/$accId"
        params={{ wsKey: props.wsKey(), accId: id }}
        class="link link-primary truncate"
      >
        {label ?? id}
      </Link>
    );
  };

  return (
    <Show when={cand()}>
      {(c) => (
        <div>
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="text-xs opacity-60">
                {subjectLabel()} <span class="opacity-50">·</span>{" "}
                <span class="badge badge-ghost badge-xs">{c().confidence ?? "?"}</span>
              </div>
              <div class="grid grid-cols-[1fr_auto_1fr] gap-2 items-center mt-1 text-sm">
                {linkFor(c().from_id, c().from_name)}
                <span class="opacity-50">→</span>
                {linkFor(c().into_id, c().into_name)}
              </div>
              <Show when={c().reason}>
                <div class="text-xs opacity-60 mt-1">{c().reason}</div>
              </Show>
            </div>
            <ApproveRejectButtons
              onApprove={props.onApprove}
              onReject={props.onReject}
              busy={props.busy}
            />
          </div>
        </div>
      )}
    </Show>
  );
}

function ApproveRejectButtons(props: {
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  return (
    <div class="flex gap-1 shrink-0">
      <button
        type="button"
        class="btn btn-success btn-xs"
        onClick={props.onApprove}
        disabled={props.busy}
        aria-label="Approve"
      >
        Approve
      </button>
      <button
        type="button"
        class="btn btn-ghost btn-xs"
        onClick={props.onReject}
        disabled={props.busy}
        aria-label="Reject"
      >
        Reject
      </button>
    </div>
  );
}
